
import { HTMLAttributes, ReactNode, useEffect, useId, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Icon, Badge, Text, Row, Col, Collapse } from '@evertel/web/ui'


interface DrawerNavItemDropdownProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
    label: string
    unreadCount?: number
    hasUrgentUnreads?: boolean
    isOpen?: boolean
    onClick?: (isOpen: boolean) => void
    // shown right after the label text, as a sibling of the header button
    // (so it can hold its own buttons) laid over the header row
    titleAccessory?: ReactNode
    children?: ReactNode
}


const DrawerNavDropdownLink: React.FC<DrawerNavItemDropdownProps> = observer(({
    id,
    label,
    onClick,
    unreadCount,
    isOpen,
    hasUrgentUnreads,
    titleAccessory,
    children,
    ...otherProps
}) => {

    const [isCollapsed, setIsCollapsed] = useState(!isOpen)

    useEffect(() => {
        setIsCollapsed(!isOpen)
    }, [isOpen])

    const onToggle = () => {
        onClick?.(isCollapsed)
        setIsCollapsed(!isCollapsed)
    }

    const contentId = useId()

    return (
        <Col
            {...otherProps}
            className={classNames(otherProps.className, { 'drawer-item-dropdown-with-accessory': titleAccessory })}
            data-drawer-container
        >
            <button
                id={id}
                className={classNames('drawer-item-dropdown drawer-item-link justify-content-between btn btn-link', {'hover-action': onClick})}
                onClick={onToggle}
                aria-expanded={!isCollapsed}
                aria-controls={contentId}
                data-drawer-nav="header"
            >
                <Row valign="center">
                    <Icon
                        name={'chevron-down'}
                        color="primary"
                        size="sm"
                        className={classNames('chevron me-3',
                            {
                                'closed': isCollapsed
                            })}
                    />
                    <Text
                        color="primary"
                        className={classNames('text-truncate',
                            {
                                'open': isOpen
                            })}>
                        {label}
                    </Text>
                </Row>
                {(!!unreadCount && isCollapsed) &&
                    <Badge
                        className="drawer-item-dropdown-badge align-self-center"
                        position="top-start"
                        color={(hasUrgentUnreads) ? 'danger' : 'warning'}
                    >
                        {unreadCount}
                    </Badge>
                }
            </button>
            {(!!titleAccessory) && (
                <div className="drawer-item-dropdown-accessory">
                    {titleAccessory}
                </div>
            )}
            <Collapse
                id={contentId}
                visible={!isCollapsed}
            >
                {children}
            </Collapse>
        </Col>
    )
})

export { DrawerNavDropdownLink }
